import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Users, 
  CreditCard, 
  BarChart3, 
  ChevronLeft, 
  ChevronRight,
  LogOut,
  Moon,
  Sun,
  ChevronDown,
  Wrench,
  Receipt,
  Repeat,
  Package,
  FileInput,
  RefreshCcw,
  Code2,
  BookOpen,
  LayoutDashboard,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { API } from '@/lib/apiBase';
import { getApplicationId } from '@/lib/applicationId';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cf623078-725f-4e36-b08f-8b0b55ed25bb/artifacts/jewke8hu_image.png";
const CENTRALE_URL = process.env.REACT_APP_CENTRALE_URL || 'https://app.prysme.eu';
const AUTH_MODE = process.env.REACT_APP_AUTH_MODE || 'local';

export const Sidebar = ({
  collapsed,
  setCollapsed,
  setHelpCenterOpen,
  helpPendingCount = 0,
  isMobile = false,
}) => {
  const { user, logout, isAdmin, isGestionnaire, isDeveloper, maintenanceMode, updateMaintenanceMode, canAccess } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [maintenanceDialogOpen, setMaintenanceDialogOpen] = useState(false);
  const [maintenanceDisableDialogOpen, setMaintenanceDisableDialogOpen] = useState(false);
  const [maintenanceReason, setMaintenanceReason] = useState('');
  const [maintenanceEnd, setMaintenanceEnd] = useState('');
  const [costsMenuOpen, setCostsMenuOpen] = useState(false);
  const [appVersion, setAppVersion] = useState(null);
  const isSSO = AUTH_MODE === 'sso';
  const unviewedCount = helpPendingCount;
  const helpFooterTitle =
    unviewedCount > 0
      ? `Centre d'aide — ${unviewedCount} à voir`
      : collapsed
        ? "Centre d'aide"
        : undefined;
  const helpFooterAriaLabel =
    unviewedCount > 0
      ? `Centre d'aide, ${unviewedCount} élément${unviewedCount > 1 ? 's' : ''} à voir`
      : undefined;

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/app-versions/sidebar`, {
        params: { application_id: getApplicationId() },
      })
      .then((res) => {
        if (!cancelled) setAppVersion(res.data?.version || null);
      })
      .catch(() => {
        if (!cancelled) setAppVersion(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleLogout = () => {
    if (isSSO) {
      // En mode SSO, retour vers la centrale Prysma
      logout();
      window.location.href = CENTRALE_URL;
    } else {
      // En mode local, déconnexion classique
      logout();
      navigate('/login');
      toast.success('Déconnexion réussie');
    }
  };

  const handleMaintenanceToggle = async () => {
    if (maintenanceMode?.is_active) {
      // Show confirmation dialog before disabling
      setMaintenanceDisableDialogOpen(true);
    } else {
      setMaintenanceDialogOpen(true);
    }
  };

  const handleMaintenanceDisable = async () => {
    try {
      await updateMaintenanceMode(false);
      setMaintenanceDisableDialogOpen(false);
      toast.success('Mode maintenance désactivé');
    } catch (error) {
      toast.error('Erreur lors de la désactivation');
    }
  };

  const handleMaintenanceSubmit = async () => {
    try {
      await updateMaintenanceMode(true, maintenanceReason, maintenanceEnd || null);
      setMaintenanceDialogOpen(false);
      setMaintenanceReason('');
      setMaintenanceEnd('');
      toast.success('Mode maintenance activé');
    } catch (error) {
      toast.error('Erreur lors de l\'activation');
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const canManage = isAdmin || isGestionnaire;

  // Navigation links with permission-based visibility
  const navLinks = [
    {
      to: '/',
      icon: LayoutDashboard,
      label: 'Tableau de bord',
      testId: 'nav-dashboard',
      permissionModule: null,
      end: true,
    },
    { 
      to: '/products', 
      icon: Package, 
      label: 'Produits', 
      testId: 'nav-products', 
      permissionModule: 'products'
    },
    { 
      to: '/entries', 
      icon: FileInput, 
      label: 'Entrées', 
      testId: 'nav-entries', 
      permissionModule: 'entries'
    },
    { 
      label: 'Coûts', 
      icon: CreditCard, 
      isMenu: true,
      testId: 'nav-costs-menu',
      subItems: [
        { to: '/subscriptions', icon: Repeat, label: 'Abonnements', testId: 'nav-subscriptions' },
        { to: '/costs', icon: Receipt, label: 'Coûts ponctuels', testId: 'nav-costs' },
      ],
      // Permission for Costs menu (includes Subscriptions and One-off costs)
      permissionModule: 'costs'
    },
    { 
      to: '/refacturation', 
      icon: RefreshCcw, 
      label: 'Refacturation', 
      testId: 'nav-refacturation',
      permissionModule: 'refacturation'
    },
    { 
      to: '/statistics', 
      icon: BarChart3, 
      label: 'Statistiques', 
      testId: 'nav-statistics',
      permissionModule: 'statistics'
    },
    { 
      to: '/users', 
      icon: Users, 
      label: 'Utilisateurs', 
      testId: 'nav-users', 
      permissionModule: 'users'
    },
    { 
      to: '/development',
      icon: Code2,
      label: 'Développement',
      testId: 'nav-development',
      permissionModule: 'development',
    },
  ];

  return (
    <>
      <aside 
        data-testid="sidebar"
        className={`${collapsed ? 'w-20' : 'w-64'} bg-card border-r border-border h-screen sticky top-0 flex flex-col transition-all duration-300 z-40`}
      >
        {/* Zone A - Logo */}
        <div className="p-4 border-b border-border">
          {collapsed ? (
            <div className="flex flex-col items-center gap-1">
              <img
                src={LOGO_URL}
                alt="Prysm'Analytics"
                className="w-10 h-10 object-contain"
                data-testid="logo"
              />
              {appVersion ? (
                <span className="text-[10px] leading-none text-muted-foreground">v {appVersion}</span>
              ) : null}
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <img
                src={LOGO_URL}
                alt="Prysm'Analytics"
                className="w-10 h-10 object-contain"
                data-testid="logo"
              />
              <div className="min-w-0">
                <span className="font-heading font-semibold text-foreground text-lg block">
                  Prysm'Analytics
                </span>
                {appVersion ? (
                  <span className="text-[11px] text-muted-foreground">v {appVersion}</span>
                ) : null}
              </div>
            </div>
          )}
        </div>

        {/* Zone B - Profil (en haut, sous le logo) */}
        <div className="p-4 border-b border-border">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button 
                className={`w-full flex items-center gap-3 p-2 rounded-lg hover:bg-secondary/50 transition-colors ${collapsed ? 'justify-center' : ''}`}
                data-testid="profile-dropdown-trigger"
              >
                {/* Photo utilisateur avec indicateur en ligne */}
                <div className="relative flex-shrink-0">
                  {user?.photo_url ? (
                    <img 
                      src={user.photo_url}
                      alt={user?.name || 'User'} 
                      className="w-10 h-10 rounded-full object-cover border-2 border-border"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-[#f76f4b] flex items-center justify-center">
                      <span className="text-white text-sm font-medium">
                        {getInitials(user?.name || user?.email)}
                      </span>
                    </div>
                  )}
                  {/* Pastille verte "en ligne" */}
                  <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-background rounded-full"></div>
                </div>
                
                {/* Nom et position */}
                {!collapsed && (
                  <div className="flex-1 text-left min-w-0">
                    <p className="text-sm font-semibold text-foreground truncate">
                      {user?.name || user?.email?.split('@')[0]}
                    </p>
                    <p className="text-xs text-muted-foreground truncate" title={user?.position || 'Utilisateur'}>
                      {user?.position || 'Utilisateur'}
                    </p>
                  </div>
                )}
              </button>
            </DropdownMenuTrigger>
            
            <DropdownMenuContent 
              align="start" 
              sideOffset={5}
              className="w-72 p-2 bg-card border-border"
            >
              {/* ========================================= */}
              {/* === 1. TOGGLE MODE SOMBRE/CLAIR ======== */}
              {/* ========================================= */}
              <div 
                className="mb-2 p-3 bg-secondary/50 rounded-lg cursor-pointer hover:bg-secondary/70 transition-colors"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleTheme();
                }}
                data-testid="dark-mode-toggle"
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {isDark ? (
                      <Moon size={18} className="text-blue-400" />
                    ) : (
                      <Sun size={18} className="text-yellow-500" />
                    )}
                    <span className="text-sm font-medium text-foreground whitespace-nowrap">
                      {isDark ? 'Mode sombre' : 'Mode clair'}
                    </span>
                  </div>
                  {/* Toggle Switch */}
                  <div
                    className={`relative w-14 h-7 rounded-full transition-colors duration-300 flex-shrink-0 ${
                      isDark ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow-md transition-transform duration-300 ${
                        isDark ? 'translate-x-8' : 'translate-x-1'
                      }`}
                    />
                  </div>
                </div>
              </div>
              
              <DropdownMenuSeparator className="my-2" />

              {/* ========================================= */}
              {/* === 2. BOUTONS EXISTANTS (Admin) ======= */}
              {/* ========================================= */}
              {isAdmin && (
                <>
                  <DropdownMenuItem 
                    onClick={handleMaintenanceToggle}
                    className="cursor-pointer rounded-lg"
                    data-testid="maintenance-toggle"
                  >
                    <Wrench className="mr-2 h-4 w-4 text-orange-500" />
                    <span>Mode Maintenance</span>
                    <span className={`ml-auto text-xs ${maintenanceMode?.is_active ? 'text-orange-500' : 'text-muted-foreground'}`}>
                      {maintenanceMode?.is_active ? 'Actif' : 'Inactif'}
                    </span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="my-2" />
                </>
              )}
              
              {/* ========================================= */}
              {/* === 3. DÉCONNEXION / RETOUR PRYSMA ====== */}
              {/* ========================================= */}
              <DropdownMenuItem 
                onClick={handleLogout} 
                className={`cursor-pointer rounded-lg ${isSSO ? 'text-primary focus:text-primary hover:text-primary/80' : 'text-red-500 focus:text-red-500 hover:text-red-400'}`}
                data-testid="logout-button"
              >
                <LogOut className="mr-2 h-4 w-4" />
                {isSSO ? 'Retour Prysma' : 'Déconnexion'}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Zone C - Navigation */}
        <nav className="flex-1 p-4 overflow-y-auto">
          <ul className="space-y-2">
            {navLinks.map((link, index) => {
              // Check permissions based on the new permission system
              if (link.permissionModule && !canAccess(link.permissionModule)) return null;

              return (
              <li key={index}>
                {link.isMenu ? (
                  <div>
                    <button
                      onClick={() => setCostsMenuOpen(!costsMenuOpen)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors ${collapsed ? 'justify-center' : ''}`}
                      data-testid={link.testId || "costs-menu-trigger"}
                    >
                      <link.icon className="w-5 h-5 flex-shrink-0" />
                      {!collapsed && (
                        <>
                          <span className="flex-1 text-left">{link.label}</span>
                          <ChevronDown className={`w-4 h-4 transition-transform ${costsMenuOpen ? 'rotate-180' : ''}`} />
                        </>
                      )}
                    </button>
                    {costsMenuOpen && !collapsed && (
                      <ul className="mt-1 ml-4 space-y-1">
                        {link.subItems.map((subItem) => (
                          <li key={subItem.to}>
                            <NavLink
                              to={subItem.to}
                              className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                                  isActive
                                    ? 'sidebar-link-active text-primary'
                                    : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
                                }`
                              }
                              data-testid={subItem.testId || `nav-${subItem.to.replace('/', '')}`}
                            >
                              <subItem.icon className="w-4 h-4 flex-shrink-0" />
                              <span>{subItem.label}</span>
                            </NavLink>
                          </li>
                        ))}
                      </ul>
                    )}
                    {collapsed && costsMenuOpen && (
                      <ul className="mt-1 space-y-1">
                        {link.subItems.map((subItem) => (
                          <li key={subItem.to}>
                            <NavLink
                              to={subItem.to}
                              className={({ isActive }) =>
                                `flex items-center justify-center p-2 rounded-lg transition-colors ${
                                  isActive
                                    ? 'sidebar-link-active text-primary'
                                    : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
                                }`
                              }
                              data-testid={`${subItem.testId}-collapsed`}
                            >
                              <subItem.icon className="w-4 h-4" />
                            </NavLink>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : (
                  <NavLink
                    to={link.to}
                    end={link.end}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                        isActive
                          ? 'sidebar-link-active text-primary'
                          : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
                      } ${collapsed ? 'justify-center' : ''}`
                    }
                    data-testid={link.testId || `nav-${link.to.replace('/', '') || 'home'}`}
                  >
                    <link.icon className="w-5 h-5 flex-shrink-0" />
                    {!collapsed && <span>{link.label}</span>}
                  </NavLink>
                )}
              </li>
            );
            })}
          </ul>
        </nav>

        {/* Zone D — pied de sidebar (aide + repli) */}
        <div className="border-t border-gray-200 dark:border-gray-700">
          <div className={cn('flex flex-col gap-2 p-3', collapsed && 'px-2')}>
            {!isMobile && (
              <button
                type="button"
                onClick={() => setHelpCenterOpen(true)}
                title={helpFooterTitle}
                aria-label={helpFooterAriaLabel}
                className={cn(
                  'w-full flex items-center gap-2 rounded-lg text-sm text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700',
                  collapsed ? 'justify-center p-2' : 'relative justify-center px-3 py-2.5',
                )}
                data-testid="help-center-sidebar-button"
              >
                <span className="relative inline-flex shrink-0">
                  <BookOpen
                    size={20}
                    className={cn(
                      'text-[#f76f4b]',
                      unviewedCount > 0 && 'motion-safe:animate-help-book-attention',
                    )}
                    aria-hidden
                  />
                  {collapsed && unviewedCount > 0 ? (
                    <span
                      className="absolute -right-1 -top-0.5 h-2 min-w-[8px] rounded-full bg-purple-500 ring-2 ring-white dark:ring-gray-800"
                      aria-hidden
                    />
                  ) : null}
                </span>
                {!collapsed ? (
                  <span className="shrink-0 whitespace-nowrap">Centre d&apos;aide</span>
                ) : null}
              </button>
            )}
            {!isMobile ? (
              <div
                className="h-px w-full shrink-0 bg-border dark:bg-gray-700"
                aria-hidden
                role="separator"
              />
            ) : null}
            <button
              type="button"
              onClick={() => setCollapsed(!collapsed)}
              className="w-full flex items-center justify-center p-2 rounded-lg text-muted-foreground transition-colors hover:bg-secondary/50 hover:text-foreground"
              data-testid="sidebar-collapse-toggle"
            >
              {collapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </aside>

      {/* Maintenance Mode Dialog */}
      <Dialog open={maintenanceDialogOpen} onOpenChange={setMaintenanceDialogOpen}>
        <DialogContent className="bg-card border-border" data-testid="maintenance-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground">Activer le Mode Maintenance</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Seuls les administrateurs pourront accéder à la plateforme.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reason" className="text-foreground">Raison</Label>
              <Input
                id="reason"
                value={maintenanceReason}
                onChange={(e) => setMaintenanceReason(e.target.value)}
                placeholder="Ex: Mise à jour du système..."
                className="bg-secondary/50 border-border"
                data-testid="maintenance-reason-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end" className="text-foreground">Fin estimée (optionnel)</Label>
              <Input
                id="end"
                type="datetime-local"
                value={maintenanceEnd}
                onChange={(e) => setMaintenanceEnd(e.target.value)}
                className="bg-secondary/50 border-border"
                data-testid="maintenance-end-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setMaintenanceDialogOpen(false)}
              data-testid="maintenance-cancel-button"
            >
              Annuler
            </Button>
            <Button 
              onClick={handleMaintenanceSubmit}
              className="bg-orange-500 hover:bg-orange-600 text-black"
              data-testid="maintenance-confirm-button"
            >
              Activer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Maintenance Disable Confirmation Dialog */}
      <Dialog open={maintenanceDisableDialogOpen} onOpenChange={setMaintenanceDisableDialogOpen}>
        <DialogContent className="bg-card border-border" data-testid="maintenance-disable-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground">Désactiver le Mode Maintenance</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Êtes-vous sûr de vouloir désactiver le mode maintenance ? 
              Tous les utilisateurs pourront à nouveau accéder à la plateforme.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
              <p className="text-sm text-orange-400">
                <strong>Attention :</strong> Cette action rendra l'application accessible à tous les utilisateurs immédiatement.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setMaintenanceDisableDialogOpen(false)}
              data-testid="maintenance-disable-cancel-button"
            >
              Annuler
            </Button>
            <Button 
              onClick={handleMaintenanceDisable}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="maintenance-disable-confirm-button"
            >
              Désactiver la maintenance
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
